import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Information } from '@mint-lib/icons';
import {
  Card,
  Divider,
  itemVariants,
  Label,
  RadioGroup,
  RadioGroupItem,
  typographyVariants,
} from '@mint-lib/tw-components';
import { Alert } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import { GranularityLevel, HeaderMapping } from '../../api/types.js';
import { initialSearchParams } from '../../constants/tablesConfig.js';
import {
  useImportHubV2,
  usePlatformCampaignLevelFF,
} from '../../hooks/ff/index.js';
import {
  useDataFilePreview,
  useIsDataFileContextFilled,
  useMapTableHeader,
} from '../../queries/index.js';
import { RichTooltip } from '../atoms/RichTooltip.jsx';
import { ConfirmChangeGranularityLevelDialog } from '../molecules/ConfirmChangeGranularityLevelDialog.jsx';
import { TableSkeletonRows } from '../molecules/DynamicTableSkeleton.jsx';
import {
  HeaderOption,
  MetricAutocomplete,
} from '../molecules/MetricAutocomplete.jsx';
import {
  Table,
  TableBody,
  TableBodyCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../molecules/TableComponents.jsx';

type ExtendedHeaderOption = HeaderOption & {
  selected: boolean;
};

type HeadersMappingTableProps = {
  dataFileId: string;
  headers: HeaderMapping[];
  headerOptions: ExtendedHeaderOption[];
  maxMetricsCount: number;
  isMetricsLimitReached: boolean;
  isLoading: boolean;
  setGranularityLevel: (payload: {
    granularityLevel: GranularityLevel;
  }) => Promise<void>;
  isSetGranularityLevelPending: boolean;
};

export const HeadersMappingTable = ({
  dataFileId,
  headers,
  headerOptions,
  isLoading,
  maxMetricsCount,
  isMetricsLimitReached,
  setGranularityLevel,
  isSetGranularityLevelPending,
}: HeadersMappingTableProps) => {
  const { t } = useTranslation('@myn/import-hub');

  const isImportHubV2 = useImportHubV2();
  const isPlatformCampaignLevelEnabled = usePlatformCampaignLevelFF();
  const isContextFilled = useIsDataFileContextFilled(dataFileId);

  const { data: dataFilePreview } = useDataFilePreview(dataFileId);

  const [granularityLevelToConfirm, setGranularityLevelToConfirm] =
    useState<null | GranularityLevel>(null);

  const hasMappedHeaders = headers.some((header) => header.mappedHeader);

  const hasPlatformOnPricingItem =
    dataFilePreview.granularityLevel === 'pricing_item' &&
    Boolean(dataFilePreview.context?.platformName);

  const handleSelectGranularityLevel =
    (selectedGranularityLevel: GranularityLevel) => () => {
      if (selectedGranularityLevel === dataFilePreview.granularityLevel) return;

      if (hasMappedHeaders || hasPlatformOnPricingItem) {
        setGranularityLevelToConfirm(selectedGranularityLevel);
        return;
      }

      confirmGranularityLevelChange(selectedGranularityLevel);
    };

  const confirmGranularityLevelChange = async (
    granularityLevel: GranularityLevel,
  ) => {
    try {
      await setGranularityLevel({ granularityLevel });
    } catch {
      /* empty */
    }
  };

  const { mutate: mapTableHeader } = useMapTableHeader({
    dataFileId: dataFileId,
    granularityLevel: dataFilePreview.granularityLevel,
  });

  const selectHeaderOption =
    (position: number, mappedHeader: string | null) =>
    (_: unknown, value: HeaderOption | null) => {
      const payload = {
        column_position: position,
        name: value ? value.name : mappedHeader,
      };

      mapTableHeader({
        id: dataFileId,
        payload,
        isDiscard: !value,
      });
    };
  const isHeadersMappingDisabled = isImportHubV2 && !isContextFilled;
  const isTableLoading = isLoading || isSetGranularityLevelPending;

  return (
    <div className="flex items-stretch gap-4">
      <div className="flex flex-[0_0_350px] flex-col gap-4">
        <Card className="w-full">
          <h6 className={cn(typographyVariants({ variant: 'h6' }), 'mb-3')}>
            {t('Data attribution level')}
          </h6>
          <RadioGroup
            value={dataFilePreview.granularityLevel}
            onValueChange={(value) =>
              handleSelectGranularityLevel(value as GranularityLevel)()
            }
            className="flex flex-col gap-2"
          >
            <Label
              variant="bodyShort01"
              data-dd-action-name="Platform level"
              className={cn(
                itemVariants({
                  selectionListItem:
                    dataFilePreview.granularityLevel === 'platform'
                      ? 'selected'
                      : 'default',
                }),
                'm-0 cursor-pointer grid-cols-[auto_1fr_auto] shadow-sm',
              )}
            >
              <RadioGroupItem value="platform" />
              <span>{t('Platform')}</span>
              <RichTooltip
                title={t('Platform level.')}
                description={t(
                  isImportHubV2
                    ? 'Data is attributed at the platform level, detailing custom metrics based on the channel where each platform is used within the selected activity.'
                    : 'Data is attributed at the platform level, detailing custom metrics based on the channel where each platform is used within the selected campaign.',
                )}
              >
                <Information className="fill-icon-secondary" />
              </RichTooltip>
            </Label>
            <Label
              variant="bodyShort01"
              data-dd-action-name="Media row level"
              className={cn(
                itemVariants({
                  selectionListItem:
                    dataFilePreview.granularityLevel === 'media_row'
                      ? 'selected'
                      : 'default',
                }),
                'm-0 cursor-pointer grid-cols-[auto_1fr_auto] pl-6 shadow-sm',
              )}
            >
              <RadioGroupItem value="media_row" />
              <span>{t('Media row')}</span>
              <RichTooltip
                title={t('Media row level.')}
                description={t(
                  isImportHubV2
                    ? 'Data is attributed at the media row level, detailing custom metrics for each specific platform within its corresponding channel in the selected activity.'
                    : 'Data is attributed at the media row level, detailing custom metrics for each specific platform within its corresponding channel in the selected campaign.',
                )}
              >
                <Information className="fill-icon-secondary" />
              </RichTooltip>
            </Label>
            <Label
              variant="bodyShort01"
              data-dd-action-name="Placement folder ID level"
              className={cn(
                itemVariants({
                  selectionListItem:
                    dataFilePreview.granularityLevel === 'pricing_item'
                      ? 'selected'
                      : 'default',
                }),
                'm-0 cursor-pointer grid-cols-[auto_1fr_auto] pl-9 shadow-sm',
              )}
            >
              <RadioGroupItem value="pricing_item" />
              <span>{t('Placement folder ID')}</span>
              <RichTooltip
                title={t('Placement folder ID level.')}
                description={t(
                  'Import performance data for one virtual platform at a time, using both standard and custom metrics.',
                )}
              >
                <Information className="fill-icon-secondary" />
              </RichTooltip>
            </Label>
            {isPlatformCampaignLevelEnabled && (
              <Label
                variant="bodyShort01"
                data-dd-action-name="Placement folder ID / Campaign ID level"
                className={cn(
                  itemVariants({
                    selectionListItem:
                      dataFilePreview.granularityLevel === 'platform_campaign'
                        ? 'selected'
                        : 'default',
                  }),
                  'm-0 cursor-pointer grid-cols-[auto_1fr_auto] pl-12 shadow-sm',
                )}
              >
                <RadioGroupItem value="platform_campaign" />
                <span>{t('Placement folder ID / Campaign ID')}</span>
                <RichTooltip
                  title={t('Placement folder ID / Campaign ID level.')}
                  description={t(
                    'Import performance data for multiple platforms at a time, using custom metrics for API-connected platforms and both standard and custom metrics for virtual platforms.',
                  )}
                >
                  <Information className="fill-icon-secondary" />
                </RichTooltip>
              </Label>
            )}
          </RadioGroup>
        </Card>
        {dataFilePreview.granularityLevel === 'media_row' && (
          <Alert
            severity="info"
            removable={false}
            title={t('Media row name mapping made simple!')}
            description={t(
              'Use the exact name or a distinct keyword for partial matching, and the system will automatically validate the media row name for accuracy.',
            )}
          />
        )}
      </div>
      <Divider orientation="vertical" />
      <div className="flex flex-1 flex-col gap-4">
        <TableContainer>
          <Table className="table-fixed">
            <TableHead>
              <TableRow>
                <TableHeaderCell className="w-1/2">
                  {t('File header')}
                </TableHeaderCell>
                <TableHeaderCell className="w-1/2">
                  {t('ARM items')}
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isTableLoading ? (
                <TableSkeletonRows
                  cellsCount={2}
                  rowsCount={initialSearchParams.itemsPerPage}
                />
              ) : (
                headers.map((row) => (
                  <TableRow hover key={row.position} tabIndex={-1}>
                    <TableBodyCell>
                      <div>{row.rawHeader}</div>
                    </TableBodyCell>
                    <TableBodyCell>
                      <RichTooltip
                        asChild={true}
                        description={t(
                          'Fill in the missing context information in the top panel to enable mapping',
                        )}
                        disabled={!isHeadersMappingDisabled}
                      >
                        <MetricAutocomplete
                          disabled={isHeadersMappingDisabled}
                          value={headerOptions.find(
                            (option) => option.name === row.mappedHeader,
                          )}
                          onChange={selectHeaderOption(
                            row.position,
                            row.mappedHeader,
                          )}
                          options={headerOptions}
                          granularityLevel={dataFilePreview.granularityLevel}
                        />
                      </RichTooltip>
                    </TableBodyCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      {granularityLevelToConfirm && (
        <ConfirmChangeGranularityLevelDialog
          onClose={() => setGranularityLevelToConfirm(null)}
          onSubmit={() => {
            confirmGranularityLevelChange(granularityLevelToConfirm);
            setGranularityLevelToConfirm(null);
          }}
          isSubmitting={isSetGranularityLevelPending}
        />
      )}
    </div>
  );
};
